// src/config/featureFlags.ts

/**
 * Parse boolean value from environment variable
 * Handles various truthy/falsy string representations
 */
function parseBooleanEnv(value: string | undefined, defaultValue: boolean): boolean {
  if (value === undefined || value === null || value === "") {
    return defaultValue;
  }

  const normalized = value.toLowerCase().trim();
  if (normalized === "true" || normalized === "1" || normalized === "yes") {
    return true;
  }
  if (normalized === "false" || normalized === "0" || normalized === "no") {
    return false;
  }

  return defaultValue;
}

/**
 * Parse string value from environment variable
 * Returns the value as-is or the default if not set
 */
function parseStringEnv(value: string | undefined, defaultValue: string): string {
  if (value === undefined || value === null || value === "") {
    return defaultValue;
  }
  return value.trim();
}

/**
 * Feature flags for controlling application-wide functionality
 * These flags allow you to enable/disable features without changing agent configurations
 *
 * Environment Variables:
 * - ENABLE_DOCUMENT_LIBRARY: Controls Document Library feature (default: false)
 * - ENABLE_DOUBLE_CHECK: Controls fact-check button feature (default: false)
 * - ENABLE_CATYLEX_LOGO: Controls Catylex partner branding display (default: false)
 * - ENABLE_BENCHMARK_MODE: Controls industry benchmark search functionality (default: false)
 * - ENABLE_LEARNING_TIPS: Controls contextual tips during AI thinking (default: true)
 * - DISABLED_TIP_FEATURES: Comma-separated IDs to hide tips for, supports 'all-agents' (default: 'none')
 */
export const FEATURE_FLAGS = {
  /**
   * Document Library feature - enables SharePoint integration and document management
   * Controlled by ENABLE_DOCUMENT_LIBRARY environment variable
   * @default false (if env var not set)
   */
  ENABLE_DOCUMENT_LIBRARY: parseBooleanEnv(process.env.ENABLE_DOCUMENT_LIBRARY, false),

  /**
   * Double Check feature - enables fact-check button on AI responses
   * Controlled by ENABLE_DOUBLE_CHECK environment variable
   * @default false (if env var not set)
   */
  ENABLE_DOUBLE_CHECK: parseBooleanEnv(process.env.ENABLE_DOUBLE_CHECK, false),

  /**
   * Catylex Logo feature - displays partner branding with external link in chat input
   * Controlled by ENABLE_CATYLEX_LOGO environment variable
   * @default false (if env var not set)
   */
  ENABLE_CATYLEX_LOGO: parseBooleanEnv(process.env.ENABLE_CATYLEX_LOGO, false),

  /**
   * Benchmark Mode feature - enables industry best practices search from trusted sources
   * Controlled by ENABLE_BENCHMARK_MODE environment variable
   * @default false (if env var not set)
   */
  ENABLE_BENCHMARK_MODE: parseBooleanEnv(process.env.ENABLE_BENCHMARK_MODE, false),

  /**
   * Learning Tips feature - shows contextual educational tips during AI thinking
   * Tips appear 40% of the time instead of rotating "Thinking..." words
   * Controlled by ENABLE_LEARNING_TIPS environment variable
   * @default true (enabled by default, will become user setting later)
   */
  ENABLE_LEARNING_TIPS: parseBooleanEnv(process.env.ENABLE_LEARNING_TIPS, true),

  /**
   * Disabled Tip Features - comma-separated list of feature IDs to hide tips for
   * Allows per-client customization of which feature tips are shown
   * Options:
   * - 'none': All tips enabled (default)
   * - 'all': Disable all feature tips (only show agent tips)
   * - 'all-agents': Disable all agent-specific tips (only show feature tips)
   * - Comma-separated: e.g., 'benchmark,all-agents' disables benchmark + all agent tips
   * Valid feature IDs: thinking, benchmark, web-search, auto-redline, prompt-coach,
   *   parallel-agents, file-upload, sharepoint, copy-button, feedback, transparency
   * Controlled by DISABLED_TIP_FEATURES environment variable
   * @default 'none'
   */
  DISABLED_TIP_FEATURES: parseStringEnv(process.env.DISABLED_TIP_FEATURES, "none"),

  /**
   * Tenant Selector feature - enables multi-tenant dropdown for testing
   * Controlled by ENABLE_TENANT_SELECTOR environment variable
   * @default true (enabled for testing)
   */
  ENABLE_TENANT_SELECTOR: parseBooleanEnv(process.env.ENABLE_TENANT_SELECTOR, true),

  /**
   * Tenant Selector enabled users - a JSON object mapping email -> the tenant
   * option ids that user may select ("*" grants all). Keys are the allow-list of
   * who sees the selector (empty/unset = all users see it, dev backward-compat).
   * Parsed by tenantSelectorAccess.ts, which throws on a non-empty, non-JSON value
   * (the legacy comma-separated format is no longer supported).
   * Controlled by TENANT_SELECTOR_ENABLED_USERS environment variable
   * @default '' (empty = allow all users)
   */
  TENANT_SELECTOR_ENABLED_USERS: parseStringEnv(process.env.TENANT_SELECTOR_ENABLED_USERS, ""),
} as const;

/**
 * Type-safe feature flag access
 */
export type FeatureFlags = typeof FEATURE_FLAGS;
